import { Box, Skeleton, TablePagination, useTheme } from "@mui/material";
import * as React from "react";
import { useLocation } from "react-router-dom";

import { type FileRecord } from "@/shared/domain/files";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import { FileSystemNode } from "@/shared/domain/files/filesystem";
import { robotDescriptionToIngest } from "@/shared/domain/urdf";

import { DatasetFileGrid } from "./DatasetFileGrid";
import { DatasetFileTable } from "./DatasetFileTable";
import { NoFilesUploadedAlert } from "./NoFilesUploadedAlert";
import { PaginationConfig } from "./state";

interface DatasetDisplaysProps {
  displayMode: string;
  datasetId: string;
  tableConfig: PaginationConfig;
  totalItemCount: number | undefined;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => void;
  onRowsPerPageChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | undefined,
  ) => void;
  currentPage: FileSystemNode[] | undefined;
  isFetching: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
  isSearching: boolean;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  gridItems: number;
  selectedItems: FileSystemNode[];
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  /**
   * Opens the ingest wizard for a robot description. Owned by the browser.
   *
   * Optional — absent when `release-3d-assets` is off, and callers pass it
   * straight through, so the components that act on it handle its absence.
   */
  onIngestAsAsset?: (file: FileRecord) => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn, direction: SortDirection) => void;
}

export const DatasetDisplays: React.FC<DatasetDisplaysProps> = ({
  displayMode,
  datasetId,
  tableConfig,
  totalItemCount,
  onPageChange,
  onRowsPerPageChange,
  currentPage,
  isFetching,
  isPending,
  isPlaceholderData,
  isSearching,
  handleDeleteRow,
  handleRowDoubleClick,
  gridItems,
  selectedItems,
  handleActionConfirmation,
  onIngestAsAsset,
  handleSelectRows,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  const theme = useTheme();

  // The grid is shared and knows nothing about robot descriptions, so the
  // feature answers its "does anyone want to open this?" question here. The
  // table's rows and menu are the browser's own, and keep calling the helper
  // directly.
  const fileViewOverride = React.useMemo(() => {
    if (onIngestAsAsset === undefined) {
      return undefined;
    }
    return {
      accepts: (node: FileSystemNode) =>
        robotDescriptionToIngest(node) !== null,
      view: (node: FileSystemNode) => {
        const urdfToIngest = robotDescriptionToIngest(node);
        if (urdfToIngest !== null) {
          onIngestAsAsset(urdfToIngest);
        }
      },
    };
  }, [onIngestAsAsset]);

  const { pathname } = useLocation();
  const cleanPathname = pathname.replace(/^\/+|\/+$/g, "");
  const inFolder = cleanPathname.split("/").length > 2;

  let content;
  if ((currentPage && currentPage.length > 0) || isSearching || inFolder) {
    content = (
      <>
        {displayMode === "table" ? (
          <DatasetFileTable
            currentPage={currentPage ?? []}
            datasetId={datasetId}
            handleDeleteRow={handleDeleteRow}
            handleRowDoubleClick={handleRowDoubleClick}
            handleActionConfirmation={handleActionConfirmation}
            onIngestAsAsset={onIngestAsAsset}
            isFetching={isFetching}
            isPlaceholderData={isPlaceholderData}
            isSearching={isSearching}
            selectedItems={selectedItems}
            handleSelectRows={handleSelectRows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
          />
        ) : (
          <DatasetFileGrid
            datasetId={datasetId}
            currentPage={currentPage ?? []}
            handleRowClick={handleRowDoubleClick}
            isSearching={isSearching}
            gridItems={gridItems}
            selectedItems={selectedItems}
            handleSelectRows={handleSelectRows}
            fileViewOverride={fileViewOverride}
            emptyContent={<NoFilesUploadedAlert datasetId={datasetId} />}
          />
        )}
        <TablePagination
          component={Box}
          count={totalItemCount ?? -1}
          page={tableConfig.page}
          rowsPerPage={tableConfig.rowsPerPage}
          labelRowsPerPage={"Items per page:"}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(event, newPage) => {
            onPageChange(event, newPage);
          }}
          onRowsPerPageChange={(event) => {
            onRowsPerPageChange(event);
          }}
        />
      </>
    );
  } else {
    content = <NoFilesUploadedAlert datasetId={datasetId} />;
  }

  if (isPending) {
    return (
      <Box sx={{ width: "100%", marginBottom: theme.spacing(1) }}>
        <Skeleton animation="wave" variant="rounded" height={150} />
      </Box>
    );
  }

  return content;
};
